// Floating Navigation for Present Mode
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface PresentModeNavigationProps {
  pages: Array<{ id: string; name: string; pageNumber: number }>;
  currentPageIndex: number;
  onPageChange: (index: number) => void;
  onExit: () => void;
}

export const PresentModeNavigation: React.FC<PresentModeNavigationProps> = ({
  pages,
  currentPageIndex,
  onPageChange,
  onExit
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [mouseIdleTimeout, setMouseIdleTimeout] = useState<NodeJS.Timeout | null>(null);

  // Show navigation on mouse movement, hide after idle
  useEffect(() => {
    const handleMouseMove = () => {
      setIsVisible(true);

      // Clear existing timeout
      if (mouseIdleTimeout) {
        clearTimeout(mouseIdleTimeout);
      }

      // Hide after 2 seconds of no mouse movement
      const timeout = setTimeout(() => {
        setIsVisible(false);
      }, 2000);

      setMouseIdleTimeout(timeout);
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (mouseIdleTimeout) {
        clearTimeout(mouseIdleTimeout);
      }
    };
  }, [mouseIdleTimeout]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && currentPageIndex > 0) {
        e.preventDefault();
        onPageChange(currentPageIndex - 1);
      } else if (e.key === 'ArrowRight' && currentPageIndex < pages.length - 1) {
        e.preventDefault();
        onPageChange(currentPageIndex + 1);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onExit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPageIndex, pages.length, onPageChange, onExit]);

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      onPageChange(currentPageIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentPageIndex < pages.length - 1) {
      onPageChange(currentPageIndex + 1);
    }
  };

  return (
    <div
      className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div className="bg-black/80 backdrop-blur-sm text-white rounded-full px-6 py-3 shadow-2xl flex items-center gap-4">
        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          disabled={currentPageIndex === 0}
          className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="Previous page (←)"
        >
          <ChevronLeft size={24} />
        </button>

        {/* Page Indicator with Dots */}
        <div className="flex items-center gap-2">
          {pages.map((page, index) => (
            <button
              key={page.id}
              onClick={() => onPageChange(index)}
              className={`transition-all ${
                index === currentPageIndex
                  ? 'w-8 h-3 bg-white rounded-full'
                  : 'w-3 h-3 bg-white/40 hover:bg-white/60 rounded-full'
              }`}
              title={page.name}
            />
          ))}
        </div>

        {/* Page Name */}
        <div className="px-4 border-l border-white/30">
          <span className="text-sm font-medium">{pages[currentPageIndex]?.name}</span>
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          disabled={currentPageIndex >= pages.length - 1}
          className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="Next page (→)"
        >
          <ChevronRight size={24} />
        </button>

        {/* Exit Button */}
        <button
          onClick={onExit}
          className="ml-2 p-2 hover:bg-white/20 rounded-full transition-colors border-l border-white/30 pl-4"
          title="Exit presentation (Esc)"
        >
          <X size={20} />
        </button>
      </div>
    </div>
  );
};
